import type { AccountView, CardView } from "../api/types.js";
import { lastDigits } from "../lib/format.js";

/**
 * DCTA-208 (final QA D3) — which card the app's OWN card phrasings mean ("Show my card details", "What's my card number?",
 * "When is my card bill due?"). They never reach the bank, so they are read by the bank's own rule for "my card" (lab/dcta
 * resolver/cards.ts, DCTA-202): the debit cards — the cards on the customer's own accounts — when there are any, else every
 * card; one is that card, two or more are ONE question, a chip per card, in the bank's order and words; none held is the
 * bank's own line. Never a default (the app used to show the credit card first). Held to the bank's answers to the same
 * words in test/card-choice.spec.ts.
 */

/** The bank's question (spoken-cta.md QN18), word for word. */
export const WHICH_CARD = "Which card?";
/** The bank's answer when no card is held (DCTA-204 R3), word for word. */
export const NO_CARD_LINE = "You don't have a card with us.";

export type CardFit =
  | { readonly kind: "none" }
  | { readonly kind: "one"; readonly card: CardView }
  | { readonly kind: "many"; readonly cards: readonly CardView[] };

const fit = (cards: readonly CardView[]): CardFit => {
  const [only] = cards;
  if (only === undefined) return { kind: "none" };
  return cards.length === 1 ? { kind: "one", card: only } : { kind: "many", cards };
};

/** "my card": the debit cards when the customer holds any, else every card they hold. */
export function myCard(cards: readonly CardView[]): CardFit {
  const debit = cards.filter((c) => c.type === "debit");
  return fit(debit.length > 0 ? debit : cards);
}

/** "my card bill": the credit cards with a statement — a card with no statement has no bill to show. */
export function cardBill(cards: readonly CardView[]): CardFit {
  return fit(cards.filter((c) => c.type === "credit" && c.statement_balance !== undefined));
}

/** One chip of "Which card?": the card, and the bank's words for it ("Debit ···5404 · Everyday Savings"). */
export interface CardChoice {
  readonly card: CardView;
  readonly label: string;
}

/**
 * The chips in the bank's order: the card on the default debit account first, the other debit cards in their accounts'
 * order (`/v1/accounts`), then the credit cards; cards of one rank keep the read's order (`/v1/cards`, the bank's own).
 */
export function cardChoices(cards: readonly CardView[], accounts: readonly AccountView[], defaultDebit: string | undefined): CardChoice[] {
  const rank = (c: CardView): number => {
    if (c.type === "credit") return accounts.length + 1;
    if (c.account_id === undefined) return accounts.length;
    if (c.account_id === defaultDebit) return -1;
    const at = accounts.findIndex((a) => a.account_id === c.account_id);
    return at < 0 ? accounts.length : at;
  };
  return [...cards]
    .sort((a, b) => rank(a) - rank(b))
    .map((card) => {
      const tail = lastDigits(card.masked_pan);
      if (card.type === "credit") return { card, label: `Credit ${tail} · ${card.display_name ?? "Credit card"}` };
      const account = accounts.find((a) => a.account_id === card.account_id);
      return { card, label: `Debit ${tail}${account === undefined ? "" : ` · ${account.display_name}`}` };
    });
}
